import {ContentContainer} from '@coolcolduk/ui-mui';
import {ArrowBack} from '@mui/icons-material';
import {Alert, Box, Button, Paper, Stack, Typography} from '@mui/material';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {QuizTimedMode} from '@cp/model';
import ErrorComponent from '../../components/ErrorComponent/ErrorComponent';
import HintDialog from '../../components/HintDialog';
import MarkdownContentRenderer from '../../atoms/MarkdownContentRenderer/MarkdownContentRenderer';
import BondGraphRenderer from '../../bonds/bondGraphRenderer/BondGraphRenderer';
import ImageDisplayFromStorage from '../../atoms/imageDisplayFromStorage/ImageDisplayFromStorage';
import {PrivateRouteProps} from '../../config/routeType';
import useEndQuizSession from '../../hooks/quizSession/useEndQuizSession';
import useGetExamModule from '../../hooks/examModules/useGetExamModule';
import useUpdateQuestionAnswer from '../../hooks/question/useUpdateQuestionAnswer';
import useSubscribeQuizSession from '../../hooks/quizSession/useSubscribeQuizSession';
import timeElapse from '../../utils/time/timeElapse';
import LoadingPage from '../other/LoadingPage';
import SessionEffect from '../session/SessionEffect';
import SessionFeedback from '../session/SessionFeedback';
import SessionNavigationHelp from '../session/SessionNavigationHelp';
import SessionPageHeader from '../session/SessionPageHeader';
import SessionQuestionAnswerList from '../session/SessionQuestionAnswerList';
import SessionQuestionHead from '../session/SessionQuestionHead';
import SessionQuestionNavigation from '../session/SessionQuestionNavigation';
import findIndexUnanswerQuestion from '../session/helper/findIndexUnanswerQuestion';
import isSessionQuestionAnswered from '../session/helper/isSessionQuestionAnswered';
import useDataNavigation from '../../hooks/helper/useDataNavigation';
import useSessionTimed from '../session/helper/useSessionTimed';
import useSessionTimedPerQuestion from '../session/helper/useSessionTimedPerQuestion';

export default function CoursePracticePage({user}: PrivateRouteProps) {
  const {examModuleId, sessionId} = useParams<{examModuleId: string; sessionId: string}>();
  const navigate = useNavigate();

  const {session, loading: sessionLoading} = useSubscribeQuizSession(sessionId || '');

  // Live session data from Firestore subscription
  const currentSession = session;

  // Get exam module from URL params
  const {
    examModule,
    loading: moduleLoading,
    error: moduleError,
  } = useGetExamModule(examModuleId ?? '', {skip: !examModuleId});

  // Functional hooks
  const {updateQuestionAnswer} = useUpdateQuestionAnswer();
  const {endQuizSession} = useEndQuizSession();

  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const selectedAnswerRef = useRef<string>('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // Data navigation
  const {
    currentData: currentQuestion,
    currentIndex: currentQuestionIndex,
    next: nextQuestion,
    previous: previousQuestion,
    isFirst: isFirstQuestion,
    isLast: isLastQuestion,
  } = useDataNavigation(currentSession?.questions || [], {
    initialIndexFinder: findIndexUnanswerQuestion,
    handleNext: (_current, next) => {
      setSelectedAnswer(next.selectedAnswer || '');
      setShowFeedback(false);
    },
    handlePrevious: (_current, previous) => {
      setSelectedAnswer(previous.selectedAnswer || '');
      setShowFeedback(false);
    },
  });

  // Keep ref in sync with state
  useEffect(() => {
    selectedAnswerRef.current = selectedAnswer;
  }, [selectedAnswer]);

  const processSubmitAnswer = useCallback(() => {
    if (!currentSession || !currentQuestion) return;

    const currentSelectedAnswer = selectedAnswerRef.current;
    setShowFeedback(true);

    updateQuestionAnswer(currentSession.id, currentQuestion.id, currentSelectedAnswer)
      .then(() => {
        // Answer updated
      })
      .catch((err: Error) => {
        console.error('Failed to update answer:', err);
        throw err;
      });
  }, [currentSession, currentQuestion, updateQuestionAnswer]);

  const handleEndSession = () => {
    if (currentSession?.id && !currentSession.endedAt) {
      endQuizSession(currentSession.id);
    }
    navigate(`/session/${sessionId}/complete`);
  };

  // Use the timed per question hook
  const {timeRemaining, startTimer, stopTimer} = useSessionTimedPerQuestion(
    currentSession?.timedMode === QuizTimedMode.TIMED_PER_QUESTION && currentSession.timeDurationSeconds
      ? currentSession.timeDurationSeconds
      : undefined,
    processSubmitAnswer,
  );

  const handleSubmitAnswer = () => {
    processSubmitAnswer();
    stopTimer();
  };

  // Use the timed mode hook
  const {timeRemaining: totalTimeRemaining} = useSessionTimed(
    currentSession?.timedMode === QuizTimedMode.TIMED ? currentSession?.startedAt : undefined,
    currentSession?.timeDurationSeconds || undefined,
    handleEndSession,
  );

  // Actions when question changes
  useEffect(() => {
    const answered = isSessionQuestionAnswered(currentQuestion) || showFeedback;
    if (answered) {
      stopTimer();
    } else {
      startTimer();
    }
  }, [currentQuestion, showFeedback, startTimer, stopTimer]);

  // Actions when session ended
  useEffect(() => {
    if (currentSession?.endedAt) {
      const elapsed = timeElapse(currentSession.endedAt);
      if (elapsed > 30) {
        navigate(`/session/${sessionId}/complete`);
      }
    }
  }, [currentSession?.endedAt, navigate, sessionId]);

  const handleAnswerSelect = (answer: string) => {
    if (showFeedback) return;
    if (currentQuestion?.selectedAnswer) return;
    setSelectedAnswer(answer);
  };

  const handleBack = () => {
    if (examModuleId) {
      navigate(`/course/${examModuleId}`);
    } else {
      navigate('/');
    }
  };

  if (moduleLoading || sessionLoading) {
    return <LoadingPage center minusAppBar />;
  }

  if (moduleError || !examModule) {
    return <ErrorComponent message="Module not found" buttonText="Go back" handleClick={handleBack} />;
  }

  if (!currentSession) {
    return <ErrorComponent message="Session not found" buttonText="Go back" handleClick={handleBack} />;
  }

  // Wait for trigger to populate questions: show loading while session has no questions and is not ended
  const hasNoQuestions = !currentSession.questions || currentSession.questions.length === 0;
  if (hasNoQuestions && !currentSession.endedAt) {
    return <LoadingPage center minusAppBar />;
  }

  if (hasNoQuestions) {
    // Get topic name from session (first topic if multiple)
    const topicName = currentSession.topics && currentSession.topics.length > 0 ? currentSession.topics[0] : 'Practice';

    return (
      <ContentContainer data-testid="course-practice-page">
        {/* Header with Back Button */}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
          <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{minWidth: 'auto'}}>
            Back
          </Button>
          <Box sx={{flex: 1}} />
        </Box>

        {/* Topic and Module Info */}
        <Paper sx={{p: 2, mb: 2, bgcolor: 'background.paper'}}>
          <Stack spacing={1}>
            <Typography variant="h5" sx={{fontWeight: 600}}>
              {topicName}
            </Typography>
            {examModule && (
              <Typography variant="body2" color="text.secondary">
                {examModule.name}
              </Typography>
            )}
          </Stack>
        </Paper>

        {/* Alert for no questions */}
        <Alert severity="warning" sx={{mb: 2}}>
          <Typography variant="body1" sx={{fontWeight: 600, mb: 1}}>
            No questions available
          </Typography>
          <Typography variant="body2">
            {/* This practice session does not contain any questions. Please try starting a new session. */}
            We cannot find any questions for this practice session. We are still working on it so please check back
            later.
          </Typography>
        </Alert>
      </ContentContainer>
    );
  }

  if (!currentQuestion) {
    return <LoadingPage center minusAppBar />;
  }

  const progress = ((currentQuestionIndex + 1) / currentSession.questions.length) * 100;
  const isQuestionAnswered = showFeedback || isSessionQuestionAnswered(currentQuestion);

  // Calculate isCorrect: prefer backend value, fallback to local calculation when showing feedback
  const getIsCorrect = (): boolean | undefined => {
    if (currentQuestion?.isCorrect !== undefined && currentQuestion?.isCorrect !== null) {
      return currentQuestion.isCorrect;
    }
    if (showFeedback) {
      return selectedAnswerRef.current === currentQuestion?.answer;
    }
    return undefined;
  };
  const isCorrect = getIsCorrect();

  // Get topic name from session (first topic if multiple)
  const topicName = currentSession.topics && currentSession.topics.length > 0 ? currentSession.topics[0] : 'Practice';

  return (
    <ContentContainer data-testid="course-practice-page">
      {/* Header with Back Button */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{minWidth: 'auto'}}>
          Back
        </Button>
        <Box sx={{flex: 1}} />
      </Box>

      {/* Session Header */}
      <SessionPageHeader
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={currentSession.questions.length}
        progress={progress}
        timedMode={currentSession.timedMode}
        timeRemaining={timeRemaining || 0}
        totalTimeRemaining={totalTimeRemaining || 0}
      />

      {/* Topic and Module Info */}
      <Paper sx={{p: 2, mb: 2, bgcolor: 'background.paper'}}>
        <Stack spacing={1}>
          <Typography variant="h5" sx={{fontWeight: 600}}>
            {topicName}
          </Typography>
          {examModule && (
            <Typography variant="body2" color="text.secondary">
              {examModule.name}
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Question Card */}
      <Paper sx={{p: 3}}>
        <Stack direction="column" gap={3}>
          <SessionQuestionHead currentQuestion={currentQuestion} />

          <ImageDisplayFromStorage imageUrl={currentQuestion?.imageUrl} />

          <BondGraphRenderer graphJsonString={currentQuestion.graphJsonString} />

          <Typography variant="h6" sx={{fontWeight: 500}} data-testid="question-text">
            <MarkdownContentRenderer content={currentQuestion?.questionText || 'Question not available'} />
          </Typography>

          <SessionQuestionAnswerList
            currentQuestion={currentQuestion}
            selectedAnswer={selectedAnswer}
            handleAnswerSelect={handleAnswerSelect}
            disabled={showFeedback || isQuestionAnswered}
          />

          <SessionFeedback
            selectedAnswer={selectedAnswerRef.current}
            currentQuestion={currentQuestion}
            showFeedback={showFeedback || undefined}
          />

          <SessionQuestionNavigation
            currentQuestion={currentQuestion}
            handleHint={() => setShowHint(true)}
            handleNextQuestion={nextQuestion}
            handlePreviousQuestion={previousQuestion}
            handleSubmitAnswer={handleSubmitAnswer}
            handleEndSession={handleEndSession}
            isAnswered={isQuestionAnswered}
            isFirstQuestion={isFirstQuestion}
            isLastQuestion={isLastQuestion}
            session={
              {
                ...currentSession,
                score: Math.round(
                  (currentSession.questions.filter((q) => q.isCorrect).length / currentSession.questions.length) * 100,
                ),
              } as import('@cp/model').QuizSession
            }
          />

          <SessionEffect isCorrect={isCorrect} user={user} />
        </Stack>
      </Paper>

      <SessionNavigationHelp timedMode={currentSession.timedMode} />

      {/* Hint Dialog */}
      <HintDialog
        open={showHint}
        onClose={() => setShowHint(false)}
        hint={currentQuestion?.hint}
        questionText={currentQuestion?.questionText || ''}
        graphJsonString={currentQuestion?.graphJsonString}
      />
    </ContentContainer>
  );
}
